import { useCallback, useEffect, useState } from 'react';
import { Field } from '@/shared/ui/Field';
import { pdvApi, type RelatorioPdv } from '../api/pdvApi';
import { formatarDataHora, formatarDinheiro, textoDoErro } from '../formato';

function hoje(): string {
  return new Date().toISOString().slice(0, 10);
}

function trintaDiasAtras(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

/**
 * Panorama do balcão.
 *
 * Bruto, devolvido e líquido aparecem em linhas separadas de propósito:
 * "vendi 12 mil e devolvi 800" diz mais sobre o mês do que "vendi 11.200",
 * e é a devolução que revela peça errada, cliente insatisfeito ou erro de
 * atendimento.
 */
export function PdvRelatorio() {
  const [inicio, setInicio] = useState(trintaDiasAtras);
  const [fim, setFim] = useState(hoje);
  const [dados, setDados] = useState<RelatorioPdv | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await pdvApi.relatorio({ inicio, fim }));
      setErro(null);
    } catch (e) {
      setErro(textoDoErro(e));
    } finally {
      setCarregando(false);
    }
  }, [inicio, fim]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <div className="pdv-relatorio">
      <div className="pdv-relatorio__filtros">
        <Field rotulo="De">
          {(id) => (
            <input
              id={id}
              className="input"
              type="date"
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
          )}
        </Field>
        <Field rotulo="Até">
          {(id) => (
            <input
              id={id}
              className="input"
              type="date"
              value={fim}
              onChange={(e) => setFim(e.target.value)}
            />
          )}
        </Field>
      </div>

      {erro && (
        <p className="pdv-aviso pdv-aviso--erro" role="alert">
          {erro}
        </p>
      )}

      {carregando && !dados && <p className="pdv-centro--vazio">Carregando…</p>}

      {dados && (
        <>
          <div className="pdv-relatorio__numeros">
            <div className="pdv-relatorio__numero">
              <span className="pdv-total__rotulo">Vendido</span>
              <strong>{formatarDinheiro(dados.totalBruto)}</strong>
              <span className="pdv-relatorio__nota">{dados.quantidadeVendas} venda(s)</span>
            </div>
            <div className="pdv-relatorio__numero">
              <span className="pdv-total__rotulo">Devolvido</span>
              <strong className="pdv-relatorio__negativo">
                {formatarDinheiro(dados.totalDevolvido)}
              </strong>
              <span className="pdv-relatorio__nota">
                {dados.quantidadeDevolucoes} devolução(ões)
              </span>
            </div>
            <div className="pdv-relatorio__numero pdv-relatorio__numero--destaque">
              <span className="pdv-total__rotulo">Líquido</span>
              <strong>{formatarDinheiro(dados.totalLiquido)}</strong>
              <span className="pdv-relatorio__nota">
                ticket médio {formatarDinheiro(dados.ticketMedio)}
              </span>
            </div>
            <div className="pdv-relatorio__numero">
              <span className="pdv-total__rotulo">No fiado</span>
              <strong>{formatarDinheiro(dados.totalFiado)}</strong>
              <span className="pdv-relatorio__nota">a receber no financeiro</span>
            </div>
          </div>

          <div className="pdv-relatorio__grade">
            <section className="pdv-fechamento__bloco">
              <h3 className="pdv-fechamento__titulo">Por forma de pagamento</h3>
              {dados.porFormaPagamento.length === 0 ? (
                <p className="pdv-relatorio__nota">Nenhum recebimento no período.</p>
              ) : (
                <dl className="pdv-fechamento__linhas">
                  {dados.porFormaPagamento.map((f) => (
                    <div key={f.formaPagamento}>
                      <dt>{f.formaPagamento}</dt>
                      <dd>{formatarDinheiro(f.valor)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>

            <section className="pdv-fechamento__bloco">
              <h3 className="pdv-fechamento__titulo">Por operador</h3>
              {dados.porColaborador.length === 0 ? (
                <p className="pdv-relatorio__nota">Nenhuma venda no período.</p>
              ) : (
                <dl className="pdv-fechamento__linhas">
                  {dados.porColaborador.map((c) => (
                    <div key={c.colaboradorId}>
                      <dt>
                        {c.colaboradorNome}
                        <span className="pdv-relatorio__nota"> · {c.quantidadeVendas} venda(s)</span>
                      </dt>
                      <dd>{formatarDinheiro(c.total)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          </div>

          <section className="pdv-fechamento__bloco">
            <h3 className="pdv-fechamento__titulo">Peças mais vendidas</h3>
            {dados.maisVendidos.length === 0 ? (
              <p className="pdv-relatorio__nota">Nenhuma peça vendida no período.</p>
            ) : (
              <div className="table-scroll">
                <table className="table table--responsive">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Peça</th>
                      <th style={{ textAlign: 'right' }}>Qtd.</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.maisVendidos.map((p) => (
                      <tr key={p.produtoId}>
                        <td className="table__muted" data-label="Código">
                          {p.codigo ?? '—'}
                        </td>
                        <td className="table__primary" data-label="Peça">
                          {p.nome}
                        </td>
                        <td data-label="Qtd." style={{ textAlign: 'right' }}>
                          {p.quantidade}
                        </td>
                        <td data-label="Total" style={{ textAlign: 'right' }}>
                          {formatarDinheiro(p.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="pdv-fechamento__nota">
              Quantidades já líquidas: o que voltou por devolução é descontado aqui.
            </p>
          </section>

          {dados.devolucoes.length > 0 && (
            <section className="pdv-fechamento__bloco">
              <h3 className="pdv-fechamento__titulo">Devoluções</h3>
              <ul className="pdv-historico__lista">
                {dados.devolucoes.map((d) => (
                  <li key={d.id} className="pdv-historico__item">
                    <div className="pdv-historico__identidade">
                      <span className="pdv-historico__numero">{d.numero}</span>
                      <span className="pdv-historico__cliente">
                        da venda {d.vendaOrigemNumero ?? '—'} · {d.clienteNome}
                      </span>
                    </div>
                    <div className="pdv-historico__meta">
                      <span>{formatarDataHora(d.data)}</span>
                      {d.motivo && <span>{d.motivo}</span>}
                    </div>
                    <strong className="pdv-historico__valor pdv-relatorio__negativo">
                      − {formatarDinheiro(d.valor)}
                    </strong>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
